import { Modal, ModalContent } from 'pobble-components/dist/modal'
import React, { useState, useMemo, useEffect } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import { apiClient } from 'pobble-components/dist/api'
import duck from './duck-rocket.gif'
import posthog from 'posthog-js'

const currencyMap = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: '$',
  GBP: '£'
}

const UpgradeButton = injectSheet({
  button: {
    margin: '24px auto 0',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    width: '160px',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  }
})(({ id, classes, children }) => {
  return (
    <Button
      minw
      darkBlue
      tall
      className={classes.button}
      onClick={() => {
        posthog.capture('user clicked moderation upsell modal cta', {
          id: id
        })
        window.location.href = `/account/membership/moderation?from=${id}`
      }}>
      {children}
    </Button>
  )
})

const UpsellBody = injectSheet({
  pitch: {
    font: 'var(--body-)',
    margin: '16px 0 0'
  },
  list: {
    flex: '1',
    padding: '0',
    listStyle: 'none',
    textAlign: 'left',
    font: 'var(--body)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& li': {
      display: 'flex',
      gap: '10px',
      fontWeight: 'bold',
      alignItems: 'center'
    },
    '& svg': {
      color: 'var(--green)',
      fontSize: '20px'
    }
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    margin: '48px 0',
    flexDirection: 'column-reverse',
    gap: '48px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      gap: '16px'
    }
  },
  bottomNote: {
    font: 'var(--body-)',
    color: 'var(--gray-)',
    margin: '16px auto 0',
    lineHeight: '24px'
  },
  edtech: {
    padding: '16px',
    borderTop: '1px solid var(--light-gray)',
    margin: '24px -32px -48px',
    '@media (min-width: 768px)': {
      margin: '24px -56px -48px'
    },
    '& img': {
      maxWidth: '200px',
      height: 'auto'
    }
  },
  testimonial: {
    padding: '32px',
    borderTop: '1px solid var(--light-gray)',
    margin: '24px -32px -48px',
    font: 'var(--body-)',
    '@media (min-width: 768px)': {
      margin: '24px -56px -48px'
    },
    '& > div': {
      display: 'flex',
      gap: '24px',
      textAlign: 'left',
      maxWidth: '560px',
      margin: '0 auto'
    },
    '& p': {
      margin: '0'
    },
    '& blockquote': {
      display: 'flex',
      margin: '0',
      flexDirection: 'column',
      gap: '8px'
    },
    '& img': {
      width: '64px',
      height: '64px',
      borderRadius: '64px'
    }
  }
})(({ classes, price, id }) => {
  return (
    <>
      <h2>
        <span role="img" aria-label="star">
          🌟
        </span>{' '}
        Discover the power of Pobble moderation with a premium subscription
        <span role="img" aria-label="star">
          🌟
        </span>
      </h2>
      <p className={classes.pitch}>
        Would you like to feel more confident in making writing assessment
        judgements?
      </p>
      <div className={classes.body}>
        <ul className={classes.list}>
          <li>
            <span role="img" aria-label="writing">
              📝
            </span>{' '}
            Access ready-made writing samples
          </li>
          <li>
            <span role="img" aria-label="school">
              🏫
            </span>
            Easily compile your own school exemplars
          </li>
          <li>
            <span role="img" aria-label="people">
              👫
            </span>{' '}
            Facilitate moderation sessions with your team and other schools
          </li>
          <li>
            <span role="img" aria-label="laptop">
              💻
            </span>{' '}
            Create and share unlimited moderation files
          </li>
          <li>
            <span role="img" aria-label="teacher">
              👩🏻‍🏫
            </span>{' '}
            Gain access to exclusive CPD opportunities
          </li>
        </ul>
        <img src={duck} alt="Duck" width="175" height="175" />
      </div>
      <div>
        All this and more, for as little as{' '}
        {price && `${price.currency}${price.price} a year`}!<br />
        Choose the right subscription for you.
      </div>
      <UpgradeButton id={id}>Get started</UpgradeButton>
      <p className={classes.bottomNote}>
        <span role="img" aria-label="lightbulb">
          💡
        </span>{' '}
        Don’t miss out - become a Pobble Premium moderation subscriber now!
        <span role="img" aria-label="lightbulb">
          💡
        </span>
      </p>
    </>
  )
})

const Upsell = injectSheet({
  content: {
    maxWidth: '680px',
    padding: '0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  holder: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '8px',
    '& > *': {
      padding: '48px 32px',
      '@media (min-width: 768px)': {
        padding: '48px 56px'
      }
    }
  },
  upsell: {
    font: 'var(--body)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& h2': {
      margin: '0'
    }
  }
})(({ classes, id }) => {
  const [subsData, setSubsData] = useState(null)

  const price = useMemo(() => {
    if (!subsData) return null
    return {
      currency: currencyMap[subsData.currency],
      price: parseInt(subsData.pobble_moderation_annual)
    }
  }, [subsData])

  useEffect(() => {
    apiClient
      .get('v3/subscriptions/marketing_data')
      .then(res => setSubsData(res.data.subscription_marketing_data))
  }, [])

  useEffect(() => {
    posthog.capture('user opened moderation upsell modal', {
      id: id
    })
  }, [id])

  return (
    <ModalContent className={classes.content}>
      <div className={classes.holder}>
        <div className={classes.upsell}>
          <UpsellBody id={id} price={price} />
        </div>
      </div>
    </ModalContent>
  )
})

export const UpsellModal = ({ isOpen, id, requestClose }) => {
  return (
    <Modal closeButton isOpen={isOpen} requestClose={requestClose}>
      <Upsell id={id} />
    </Modal>
  )
}
